import { useMediaQuery, useTheme } from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { axiosInstance } from '../../../../api'
import { Loader } from '../../../../components/loader'
import { nonprofitsApi } from '../../../../services/api/nonprofits'
import { trackException } from '../../../../store/app-insights/reducer'
import { useNonprofitCategoriesStyles } from './nonprofit-categories.styles'
import './style.css'

// Settings for embla-carousel
const OPTIONS = {
  align: 'start' as const,
  loop: false,
  skipSnaps: false,
  dragFree: true,
  // Number of slides to scroll at once
  slidesToScroll: 1,
}

type Category = {
  nonprofitCategoryId: number
  name: string
}

export const NonprofitCategoriesSlider: React.FC = () => {
  const classes = useNonprofitCategoriesStyles()
  const theme = useTheme()
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  // Determine screen size
  const isXs = useMediaQuery(theme.breakpoints.down('xs'))
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isMd = useMediaQuery(theme.breakpoints.down('md'))
  const isLg = useMediaQuery(theme.breakpoints.down('lg'))

  // Dynamic settings based on screen size
  const getSlidesToScroll = () => {
    if (isXs) return 1 // On very small screens scroll by 1
    if (isSm) return 2 // On small screens scroll by 2
    if (isMd) return 3 // On medium screens scroll by 3
    if (isLg) return 4 // On large screens scroll by 4
    return 5 // On very large screens scroll by 5
  }

  const dynamicOptions = {
    ...OPTIONS,
    slidesToScroll: getSlidesToScroll(),
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(dynamicOptions)
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi]
  )

  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi]
  )

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  )

  const onSelect = useCallback((emblaApi: any) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)
  }, [emblaApi, onSelect])

  useEffect(() => {
    nonprofitsApi
      .categories()
      .then((categories) => {
        setCategories(categories)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [])

  return (
    <div className={classes.root}>
      <div className={classes.sliderHoder}>
        {loading ? (
          <Loader />
        ) : (
          <div className={classes.embla}>
            <div className={classes.emblaViewport} ref={emblaRef}>
              <div className={classes.emblaContainer}>
                {categories.map((category, index) => (
                  <div className={classes.emblaSlide} key={index}>
                    <Item
                      title={category.name}
                      nonprofitCategoryId={category.nonprofitCategoryId}
                    />
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              className={`${classes.emblaButton} ${classes.emblaButtonPrev}`}
              onClick={scrollPrev}
              disabled={prevBtnDisabled}
              aria-label="Previous slide"
            >
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className={`${classes.emblaButton} ${classes.emblaButtonNext}`}
              onClick={scrollNext}
              disabled={nextBtnDisabled}
              aria-label="Next slide"
            >
              <ChevronRightIcon />
            </button>
            <div className={classes.emblaDots}>
              {scrollSnaps.map((_, index) => (
                <button
                  type="button"
                  key={index}
                  className={`${classes.emblaDot} ${
                    index === selectedIndex ? classes.emblaDotActive : ''
                  }`}
                  aria-label={`Go to slide ${index + 1} of ${scrollSnaps.length}`}
                  aria-current={index === selectedIndex ? 'true' : undefined}
                  onClick={() => scrollTo(index)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Slider Item
const Item: React.FC<{
  // img: string
  title: string
  nonprofitCategoryId: number
}> = (props) => {
  const { title, nonprofitCategoryId } = props
  const ref = useRef(null)
  const classes = useNonprofitCategoriesStyles()
  const [imageUrl, setImageUrl] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(true)
  const [imgError, setImgError] = useState<boolean>(false)
  const dispatch = useDispatch()
  const src = `categories/image/${nonprofitCategoryId}/medium.jpg`

  const goToCategorySearch = () => {
    window.open(`/search?nonprofitCategoryId=${nonprofitCategoryId}`, "_self")
  }

  useEffect(() => {
    setLoading(true)
    setImgError(false)
    // @ts-ignore
    ref!.current!.style.backgroundImage = 'none'
    axiosInstance
      .get(src, { responseType: 'blob' })
      .then((response) => response.data)
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        setImageUrl(url)
        // @ts-ignore
        ref!.current!.style.backgroundImage = `url(${url})`
      })
      .catch((e) => {
        setImgError(e.response.status === 404 ? false : true)
        dispatch(
          trackException({ e, source: 'getBlodDataForImageInSliderMainPage' })
        )
      })
      .finally(() => {
        setLoading(false)
      })
  }, [nonprofitCategoryId])

  return (
    <div className={classes.sliderItem} onClick={() => goToCategorySearch()}>
      <div className={classes.sliderImgWrapper}>
        {/* If 404 error for image */}
        <div className={classes.imgError}>
          {loading ? (
            <Loader />
          ) : imgError && !imageUrl ? (
            <ErrorOutlineIcon fontSize={'large'} />
          ) : (
            <PhotoCameraIcon fontSize={'large'} />
          )}
        </div>
        <div
          className={classes.imgBlock}
          ref={ref}
          style={{ backgroundImage: `url(${imageUrl})` }}
        ></div>
      </div>
      <div className={classes.sliderItemTitle}>{title}</div>
    </div>
  )
}
